import { useState } from "react";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "../Reveal";
import api, { formatApiErrorDetail } from "../../lib/api";

const VEHICLE_TYPES = ["Sedan", "SUV", "Truck / Pickup", "Luxury Vehicle", "Motorcycle", "Heavy Equipment", "Not shipping a vehicle"];
const CARGO_TYPES = ["Personal Effects", "Household Items", "Commercial Goods", "Business Equipment", "20ft Container", "40ft Container", "Not shipping cargo"];
const US_PORTS = ["New York / New Jersey", "Los Angeles", "Seattle", "Houston", "Savannah", "Other US location"];

const empty = {
  name: "", phone: "", email: "", shipping_from: "", shipping_to: "Tema, Ghana",
  vehicle_type: "", cargo_type: "", preferred_date: "", notes: "",
};

const field = "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-navy outline-none transition focus:border-navy focus:ring-2 focus:ring-navy/20";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-navy";

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      toast.success("Quote request sent! We'll get back to you shortly.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative overflow-hidden bg-navy py-24" data-testid="quote-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(214,31,38,0.15),transparent_55%)]" />
      <div className="relative mx-auto grid max-w-7xl items-start gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.4fr] lg:px-8">
        <Reveal>
          <SectionHeading light eyebrow="Get a Quote" title="Request your free shipping quote" />
          <p className="mt-5 text-base leading-relaxed text-white/70">
            Tell us what you're shipping and where from. Our team responds fast with a transparent,
            all-in price — no hidden fees.
          </p>
          <ul className="mt-8 space-y-3">
            {["Free, no-obligation quote", "Fast response, usually within hours", "Door-to-port & port-to-port options"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm font-medium text-white/80">
                <CheckCircle2 className="h-5 w-5 text-gold" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl shadow-black/20 sm:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Name *</label>
                <input required value={form.name} onChange={update("name")} data-testid="quote-name" className={field} placeholder="Your full name" />
              </div>
              <div>
                <label className={labelCls}>Phone Number *</label>
                <input required value={form.phone} onChange={update("phone")} data-testid="quote-phone" className={field} placeholder="+1 / +233 ..." />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Email *</label>
                <input required type="email" value={form.email} onChange={update("email")} data-testid="quote-email" className={field} placeholder="you@email.com" />
              </div>
              <div>
                <label className={labelCls}>Shipping From *</label>
                <select required value={form.shipping_from} onChange={update("shipping_from")} data-testid="quote-from" className={field}>
                  <option value="">Select US port / location</option>
                  {US_PORTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Shipping To</label>
                <input value={form.shipping_to} onChange={update("shipping_to")} data-testid="quote-to" className={field} />
              </div>
              <div>
                <label className={labelCls}>Vehicle Type</label>
                <select value={form.vehicle_type} onChange={update("vehicle_type")} data-testid="quote-vehicle" className={field}>
                  <option value="">Select vehicle type</option>
                  {VEHICLE_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Cargo Type</label>
                <select value={form.cargo_type} onChange={update("cargo_type")} data-testid="quote-cargo" className={field}>
                  <option value="">Select cargo type</option>
                  {CARGO_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Preferred Shipping Date</label>
                <input type="date" value={form.preferred_date} onChange={update("preferred_date")} data-testid="quote-date" className={field} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Additional Notes</label>
                <textarea rows={3} value={form.notes} onChange={update("notes")} data-testid="quote-notes" className={field} placeholder="Anything else we should know?" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-6 py-4 text-base font-bold text-white shadow-lg shadow-brandred/30 transition-transform hover:bg-brandred-700 active:scale-95 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send size={18} />}
              {done ? "Send Another Request" : "Request My Quote"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
